import Sidebar from "../../components/Sidebar/Sidebar";
import { useLocation, useNavigate } from "react-router-dom";
import "katex/dist/katex.min.css";
import Latex from "react-latex-next";
import { useEffect, useState } from "react";
import "./AssignmentPage.scss";
import axiosClient from "../../axiosClient";
import { Assignment, FlaggedQuestion, Question } from "../../types";
import { toggleStarQuestion } from "../../utils/starQuestion";
import { ZoomImage } from "../../components/ZoomImage/ZoomImage";
import { shouldIgnoreQuestionNavigationShortcut } from "../../utils/shouldIgnoreQuestionNavigationShortcut";
import { AssignmentPageTitle } from "./AssignmentPageTitle";
import AssignmentPageSidebar from "../../components/AssignmentPageSidebar/AssignmentPageSidebar";
import toast from "react-hot-toast";
import { SjtPracticeBlock } from "../../components/SJT/SjtPracticeBlock";
import { BsArrowCounterclockwise, BsLightbulbFill, BsXCircle } from "react-icons/bs";
import { EmqPartStepper } from "../../components/EmqPartStepper/EmqPartStepper";
import { buildEmqPartStatuses } from "../../utils/buildEmqPartStatuses";
import { isQuestionFlagged } from "../../utils/flagFeedback";

const AssignmentPage = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [assignment, setAssignment] = useState<Assignment>();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const ABCDE = "ABCDE";
  // selectedAnswers maps each questionId to the answerId chosen
  const [selectedAnswers, setSelectedAnswers] = useState<{
    [key: number]: number;
  }>({});
  // For SJT questions, track ordered answer option ids by question id
  const [sjtOrderedAnswers, setSjtOrderedAnswers] = useState<{
    [key: number]: number[];
  }>({});
  const [eliminatedMcqAnswerOptionIdsByQuestionId, setEliminatedMcqAnswerOptionIdsByQuestionId] =
    useState<Record<number, Set<number>>>({});

  const navigate = useNavigate();
  const data = useLocation();

  const getSubmissionId = () => data?.state?.submissionId;
  const getAssignmentId = () => data?.state?.assignmentId;
  const getAssignmentTitle = () => data?.state?.assignmentName;

  useEffect(() => {
    console.log(questions);
  }, [questions]);

  useEffect(() => {
    const element = document.getElementById(`q${currentQuestionIndex + 1}`);
    element?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [currentQuestionIndex]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (shouldIgnoreQuestionNavigationShortcut(e)) {
        return;
      }

      if (e.key === "ArrowLeft") {
        if (currentQuestionIndex > 0) {
          setCurrentQuestionIndex(currentQuestionIndex - 1);
          e.preventDefault();
        }
      } else if (e.key === "ArrowRight") {
        if (currentQuestionIndex < questions.length - 1) {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
          e.preventDefault();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [currentQuestionIndex, questions.length]);

  const toggleMcqSelectionByAnswerOptionId = (
    questionId: number,
    answerOptionId: number,
    checkboxWasDirectlyClicked = false,
  ) => {
    if (eliminatedMcqAnswerOptionIdsByQuestionId[questionId]?.has(answerOptionId)) {
      return;
    }
    const wrapper = document.querySelector(".answer-options-wrapper");
    if (!wrapper) return;

    wrapper.querySelectorAll(".answer-option").forEach((answerOption) => {
      (answerOption as HTMLDivElement).classList.remove("selected-answer-option");
    });

    wrapper.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      (checkbox as HTMLInputElement).checked = false;
    });

    const selectedCard = document.getElementById(answerOptionId.toString());
    const checkboxInsideCard = selectedCard?.querySelector(
      'input[type="checkbox"]',
    ) as HTMLInputElement | null;

    if (!selectedCard || !checkboxInsideCard) return;

    if (!checkboxWasDirectlyClicked) {
      checkboxInsideCard.checked = !checkboxInsideCard.checked;
    }

    if (checkboxInsideCard.checked) {
      selectedCard.classList.add("selected-answer-option");
      setSelectedAnswers((prev) => ({ ...prev, [questionId]: answerOptionId }));
    } else {
      setSelectedAnswers((prev) => {
        const next = { ...prev };
        delete next[questionId];
        return next;
      });
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isLoading || questions.length === 0) return;
      const currentQuestion = questions[currentQuestionIndex];
      if (!currentQuestion) return;
      if (isSjtQuestion(currentQuestion)) return;
      if (shouldIgnoreQuestionNavigationShortcut(e)) {
        return;
      }

      const upper = e.key?.toUpperCase?.();
      const index = ABCDE.indexOf(upper);
      if (index >= 0 && index < (currentQuestion.answerOptions?.length || 0)) {
        const ao = currentQuestion.answerOptions[index];
        if (!eliminatedMcqAnswerOptionIdsByQuestionId[currentQuestion.id]?.has(ao.id)) {
          toggleMcqSelectionByAnswerOptionId(currentQuestion.id, ao.id, false);
        }
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ABCDE, currentQuestionIndex, eliminatedMcqAnswerOptionIdsByQuestionId, isLoading, questions]);

  const toggleEliminatedMcqAnswerOption = (questionId: number, answerOptionId: number) => {
    setEliminatedMcqAnswerOptionIdsByQuestionId((prev) => {
      const next: Record<number, Set<number>> = { ...prev };
      const existing = next[questionId] ? new Set(next[questionId]) : new Set<number>();
      if (existing.has(answerOptionId)) {
        existing.delete(answerOptionId);
      } else {
        existing.add(answerOptionId);
      }
      next[questionId] = existing;
      return next;
    });

    if (selectedAnswers[questionId] === answerOptionId) {
      toggleMcqSelectionByAnswerOptionId(questionId, answerOptionId, false);
    }
  };

  useEffect(() => {
    const getQuestions = async () => {
      try {
        // TODO: implement option submissionId or assignmentId in URL query parameters, instead of Link props values below
        // Else block - this is the case when the assignment is a current assignment which the student is yet to submit.
        // TODO: this is confusing. We GET /assignments and /submissions, but submit to /assignments.
        // Perhaps merge GET endpoints into one?
        const assignmentResp: {
          assignment: Assignment;
          questions: Question[];
        } = (await axiosClient.get("/assignments/" + getAssignmentId())).data;

        setAssignment(assignmentResp.assignment);

        // this helps the timer not keep re-loading the time remaining to the assignment duration when the user answers questions/changes question,
        // because it fixes the start time so the duration calculated is always fixed too from this start time, instead of being relative to the
        // current timestamp on every question answered / question changed.
        const assignmentStartTimeKey = `master_q_assignment_${assignmentResp.assignment.id}_start_time`;
        if (!localStorage.getItem(assignmentStartTimeKey)) {
          localStorage.setItem(
            assignmentStartTimeKey,
            new Date().getTime().toString(),
          );
        }

        setQuestions(assignmentResp.questions);
        setIsLoading(false);
      } catch (err) {
        toast.error("Error loading assignment. Please seek support!");
        console.error(err);
      }
    };
    getQuestions();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoading && questions.length > 0) {
      // clear all checkboxes
      document
        .querySelectorAll('input[type="checkbox"]')
        .forEach((checkbox) => {
          (checkbox as HTMLInputElement).checked = false;
        });

      document.querySelectorAll(".answer-option").forEach((answerOption) => {
        (answerOption as HTMLDivElement).classList.remove(
          "selected-answer-option",
        );
      });

      let currentQuestion = questions[currentQuestionIndex];
      // if navigating to a question already answered, re-check the necessary checkbox
      if (currentQuestion.id in selectedAnswers) {
        const selectedAnswerId = selectedAnswers[currentQuestion.id];
        const selectedAnswer = document.getElementById(
          selectedAnswerId.toString(),
        );
        const checkbox = selectedAnswer?.querySelector(
          'input[type="checkbox"]',
        );

        if (checkbox) {
          selectedAnswer?.classList.add("selected-answer-option");
          (checkbox as HTMLInputElement).checked = true;
        }
      }

      // Initialise SJT ranking to the default A→E order so the student can drag to reorder
      if (isSjtQuestion(currentQuestion) && !sjtOrderedAnswers[currentQuestion.id]) {
        setSjtOrderedAnswers((prev) => ({
          ...prev,
          [currentQuestion.id]: currentQuestion.answerOptions.map((a) => a.id),
        }));
      }
    }
  });

  if (isLoading) {
    return (
      <div className="page-wrapper" id="AssignmentPage">
        <Sidebar />
        <div className="internal-page-wrapper">Loading...</div>
      </div>
    );
  }

  const handleQuestionChange = (e: { selected: number }) => {
    const newQuestionIndex = e.selected;
    setCurrentQuestionIndex(newQuestionIndex);
  };
  const isSjtQuestion = (question: Question) => {
    return (
      question?.type?.toLowerCase?.() === "sjt" ||
      (Array.isArray(question?.answerOptions) &&
        question.answerOptions.some((a) => typeof a.correctRank === "number"))
    );
  };

  const setSjtSelection = (questionId: number, newSelection: number[]) => {
    setSjtOrderedAnswers((prev) => ({ ...prev, [questionId]: newSelection }));
  };

  const handleStarQuestion = async () => {
    const currentQuestion = questions[currentQuestionIndex];
    try {
      const responseData = await toggleStarQuestion(currentQuestion.id);

      const updatedQuestions = questions.map((q) =>
        q.id === currentQuestion.id
          ? {
            ...q,
            starredBy: responseData.starredQuestion ? [responseData.starredQuestion] : [],
          }
          : q,
      );
      setQuestions(updatedQuestions);
      toast.success(responseData.message);
    } catch (err) {
      toast.error("Sorry, error occurred starring this question. Please try again or seek support.");
    }
  };

  const applyFlaggedState = (flaggedQuestion: FlaggedQuestion) => {
    const currentQuestion = questions[currentQuestionIndex];
    const updatedQuestions = questions.map((q) =>
      q.id === currentQuestion.id
        ? {
            ...q,
            flaggedBy: [flaggedQuestion],
            flaggedComments: [flaggedQuestion],
          }
        : q,
    );
    setQuestions(updatedQuestions);
  };

  const handleFlagged = (flaggedQuestion: FlaggedQuestion) => {
    applyFlaggedState(flaggedQuestion);
  };


  const handleAnswerSelected = (e: any) => {
    const userClickedOnSurroundingDivToZoomOutOfImage = e.target.hasAttribute(
      "data-rmiz-modal-content",
    );
    // Avoid clicks on the image from changing the checked value, because clicking on the image
    // is done to enlarge it, not necessarily to select that answer.
    // ALSO avoid clicks on the div surrounding the enlarged image from changing the checked value.
    if (
      e.target.tagName === "IMG" ||
      userClickedOnSurroundingDivToZoomOutOfImage
    ) {
      return;
    }

    // See https://stackoverflow.com/a/53815609 for why we use e.currentTarget instead of e.target
    const selectedCard = e.currentTarget as Element;
    toggleMcqSelectionByAnswerOptionId(
      currentQuestion.id,
      Number(selectedCard.id),
      e.target.tagName === "INPUT",
    );
  };

  const submit = async () => {
    const mcqAnswers = Object.keys(selectedAnswers).map((questionId: string) => {
      const answerOptionId = selectedAnswers[Number(questionId)];
      return {
        questionId: Number(questionId),
        answerOptionId,
      };
    });

    const sjtAnswers = questions
      .filter((q) => isSjtQuestion(q))
      .map((q) => {
        const ordered = sjtOrderedAnswers[q.id] || [];
        if (!ordered.length) return null;
        return {
          questionId: q.id,
          orderedAnswerOptionIds: ordered,
        };
      })
      .filter((x): x is { questionId: number; answerOptionId: number; type: 'sjt'; orderedAnswerOptionIds: number[] } => !!x);

    const submittedAnswers = [...mcqAnswers, ...sjtAnswers];

    const payload = {
      // TODO: these two props should be read from props
      submissionId: getSubmissionId(),
      submittedAnswers,
    };

    try {
      const resp = await axiosClient.put(
        `/assignments/${getAssignmentId()}/submit`,
        payload,
      );

      if (resp.status === 200) {
        toast.success("Assignment complete!");
        navigate(`/submissions/${getSubmissionId()}`);
      } else {
        toast.error("Failed!");
      }
    } catch (err) {
      toast.error("Failed! Please screenshot this error and seek support.");
    }
  };

  const submitWithCheckForIncompleteQuestions = async () => {
    const mcqAnsweredCount = Object.keys(selectedAnswers).length;
    const sjtAnsweredCount = questions.filter((q) => {
      if (!isSjtQuestion(q)) return false;
      const sel = sjtOrderedAnswers[q.id] || [];
      return sel.length === q.answerOptions.length; // consider SJT answered only when fully ranked
    }).length;
    if (mcqAnsweredCount + sjtAnsweredCount < questions.length) {
      const isOkToSubmitWithoutAllAnswers = window.confirm(
        "You haven't answered all questions. Are you sure you want to submit?",
      );

      if (!isOkToSubmitWithoutAllAnswers) {
        return;
      }
    }

    submit();
  };

  const currentQuestion = questions[currentQuestionIndex];

  const delimeters = [
    {
      // NOTE: `display` is by default `true` for the '$$' delimeter option
      // out the box from the react-latex-next package. However, I've set it
      // to `false` to ensure it doesn't render the equation in the centre
      // of the parent. Instead, `false` ensures the equation is rendered
      // in-line with the text. I should also point out that I could have just
      // used the single '$' delimeter option, where all questions with equations
      // have the equation wrapped with a single '$' either side, but I thought
      // this was risky if by accident a question has a $ sign in the question
      // text itself. In such a case, we might not realise, and the question
      // would render incorrectly. Thus, I have opted for the '$$' approach,
      // despite it meaning that we have to manually define the delimeters in
      // our implementation (which wouldn't have been the case otherwise).
      // See: https://www.npmjs.com/package/react-latex-next
      display: false,
      left: "$$",
      right: "$$",
    },
  ];

  return (
    <div className="page-wrapper" id="AssignmentPage">
      <Sidebar />
      <div className="internal-page-wrapper">
        <main>
          <div className="inner-main-wrapper row">
            <div
              className="inner-max-width-wrapper col-10"
              style={{
                position: "sticky",
                paddingRight: 0,
              }}
            >
              <div className="top-area">
                <AssignmentPageTitle
                  question={currentQuestion}
                  questionNumber={currentQuestionIndex + 1}
                  totalQuestions={questions.length}
                  isAssignmentMode={true}
                  handleStarQuestion={handleStarQuestion}
                  onFlagged={handleFlagged}
                  isStarred={currentQuestion.starredBy && currentQuestion.starredBy.length > 0}
                  isFlagged={isQuestionFlagged(currentQuestion)}
                  submitAssignment={submitWithCheckForIncompleteQuestions}
                  onNextQuestionClicked={() => {
                    handleQuestionChange({
                      selected: currentQuestionIndex + 1,
                    });
                  }}
                  onPreviousQuestionClicked={() => {
                    handleQuestionChange({
                      selected: currentQuestionIndex - 1,
                    });
                  }}
                  nextQuestionButtonDisabled={
                    currentQuestionIndex === questions.length - 1
                  }
                  prevQuestionButtonDisabled={currentQuestionIndex === 0}
                />
              </div>

              <div id="scroller">
                <div
                  style={{
                    display: "flex",
                    gap: 24,
                    alignItems: "stretch",
                    backgroundColor: "#f8f9fa",
                    padding: "30px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div className="questionTitle" style={{ lineHeight: 1.5 }}>
                      {currentQuestion.emqPart?.partOrder != null &&
                      currentQuestion.emqPart?.partsCount != null ? (
                        <EmqPartStepper
                          partOrder={currentQuestion.emqPart.partOrder}
                          partsCount={currentQuestion.emqPart.partsCount}
                          partStatuses={buildEmqPartStatuses({
                            siblings: questions.filter(
                              (q) =>
                                q.emqPart?.groupingId ===
                                currentQuestion.emqPart?.groupingId,
                            ),
                            currentPartOrder: currentQuestion.emqPart.partOrder,
                            selectedAnswers,
                          })}
                        />
                      ) : null}
                      <Latex delimiters={delimeters}>{currentQuestion.text}</Latex>
                    </div>

                    {currentQuestion.imageUrl && (
                      <div style={{ marginBottom: "50px" }}>
                        <ZoomImage src={currentQuestion.imageUrl} width={300}></ZoomImage>
                      </div>
                    )}

                    {isSjtQuestion(currentQuestion) ? (
                      <SjtPracticeBlock
                        question={currentQuestion}
                        practiceQuestionAnswered={false}
                        selection={sjtOrderedAnswers[currentQuestion.id] || []}
                        delimeters={delimeters}
                        onSelectionChange={(newSel) =>
                          setSjtSelection(currentQuestion.id, newSel)
                        }
                        onSubmit={(ordered) =>
                          setSjtSelection(currentQuestion.id, ordered)
                        }
                        hideBottomSubmitButton={true}
                        hideBottomClearButton={false}
                      />
                    ) : (
                      <div className="answer-options-wrapper">
                        {currentQuestion?.answerOptions.map((answerOption, index) => {
                          const option = ABCDE[index];
                          const classes = ["answer-option"];
                          const isEliminated =
                            eliminatedMcqAnswerOptionIdsByQuestionId[currentQuestion.id]?.has(
                              answerOption.id,
                            ) ?? false;
                          return (
                            <div
                              className={classes.join(" ")}
                              onClick={handleAnswerSelected}
                              id={answerOption.id.toString()}
                            >
                              <div>
                                <div
                                  className="top-part"
                                  style={{ display: "flex", alignItems: "center" }}
                                >
                                  <label htmlFor={option}>{option}</label>
                                  <button
                                    type="button"
                                    aria-label={isEliminated ? "Uncross option" : "Cross out option"}
                                    title={isEliminated ? "Uncross option" : "Cross out option"}
                                    onClick={(ev) => {
                                      ev.stopPropagation();
                                      toggleEliminatedMcqAnswerOption(
                                        currentQuestion.id,
                                        answerOption.id,
                                      );
                                    }}
                                    style={{
                                      marginLeft: 8,
                                      border: "none",
                                      background: "transparent",
                                      padding: 2,
                                      display: "inline-flex",
                                      alignItems: "center",
                                      cursor: "pointer",
                                    }}
                                  >
                                    {isEliminated ? (
                                      <BsArrowCounterclockwise size={16} color="#cc0000" />
                                    ) : (
                                      <BsXCircle size={16} color="#666" />
                                    )}
                                  </button>
                                  <div
                                    style={{
                                      marginLeft: "10px",
                                      flex: 1,
                                      textAlign: "left",
                                    }}
                                  >
                                    <span
                                      style={
                                        isEliminated
                                          ? { textDecoration: "line-through", color: "#a33" }
                                          : undefined
                                      }
                                    >
                                      <Latex delimiters={delimeters}>
                                        {answerOption.text}
                                      </Latex>
                                    </span>
                                  </div>
                                  <input
                                    type="checkbox"
                                    name={option}
                                    id={option}
                                    style={{ display: "block" }}
                                  />
                                </div>
                                {answerOption.imageUrl && (
                                  <div className="bottom-part">
                                    <ZoomImage
                                      src={answerOption.imageUrl}
                                      width={200}
                                    ></ZoomImage>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ flex: 1, display: "grid" }}>
                  <div style={{ margin: "auto", width: "100%" }}>
                    <div style={{ display: "grid", gap: 10 }}>
                      <div style={{ margin: "0 auto" }}>
                        <BsLightbulbFill size={34} color="orange" />
                      </div>
                      <p style={{ margin: 0, textAlign: "center" }}>
                        Explanation is shown after exam submission.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-2" style={{ paddingLeft: 0 }}>
              <AssignmentPageSidebar
                currentQuestionIndex={currentQuestionIndex}
                setCurrentQuestionIndex={setCurrentQuestionIndex}
                selectedAnswers={selectedAnswers}
                questions={questions}
                assignment={assignment}
                submit={submit}
              />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AssignmentPage;
